/** 管理者アプリ データアクセス — Supabase直叩き（RLSの is_admin() で全件アクセス）。 */
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from "./supabase";

export type VendorStatus = "pending" | "approved" | "suspended" | "rejected";
export type Role = "customer" | "vendor" | "admin";

export interface VendorRow {
  id: string;
  company: string | null;
  representative: string | null;
  area: string | null;
  status: VendorStatus;
  rating: number | null;
  review_count: number | null;
  created_at: string;
  email?: string | null;
  name?: string | null;
}

export interface CaseRow {
  id: string;
  case_no: number | null;
  status: string;
  ac_type: string | null;
  ai_model_guess: string | null;
  ai_capacity: string | null;
  ai_ceiling_height: string | null;
  ai_piping: string | null;
  ai_power: string | null;
  customer_comment: string | null;
  prefecture: string | null;
  city: string | null;
  awarded_amount: number | null;
  created_at: string;
}

/** 案件の編集可能フィールド（管理者のみ）。 */
export type CasePatch = Partial<
  Pick<
    CaseRow,
    | "status"
    | "ac_type"
    | "ai_model_guess"
    | "ai_capacity"
    | "ai_ceiling_height"
    | "ai_piping"
    | "ai_power"
    | "customer_comment"
    | "prefecture"
    | "city"
  >
>;

export interface ProfileRow {
  id: string;
  email: string | null;
  name: string | null;
  role: Role;
  prefecture: string | null;
  created_at: string;
}

/** メーカー直発注（落札時に自動生成）。 */
export interface OrderRow {
  id: string;
  case_id: string;
  case_no?: number | null;
  maker: string | null;
  maker_id?: string | null;
  model: string | null;
  qty: number | null;
  ship_to: "vendor" | "customer";
  recipient_name: string | null;
  recipient_address: string | null;
  status: "draft" | "sent" | "shipped" | "received" | "cancelled";
  created_at: string;
}

/** メーカー（仕入先）マスタ。運営管理画面でCRUD（sql/15_makers.sql）。 */
export interface MakerRow {
  id: string;
  name: string;
  contact: string | null;
  note: string | null;
  created_at: string;
}
export type MakerInput = { name: string; contact?: string | null; note?: string | null };

/** 決済（収納代行型 状態機械: pending/held(預り)/released(振分済)/refunded）。 */
export type PaymentStatus = "pending" | "held" | "released" | "refunded";
export interface PaymentRow {
  id: string;
  case_id: string;
  case_no?: number | null;
  amount: number;
  fee_amount: number;
  vendor_payout: number;
  status: PaymentStatus;
  created_at: string;
  payout_at: string | null;
}

/** 外注費の支払（元請モデル：顧客決済済み released の vendor_payout を業者へ振込）。 */
export interface PayoutRow {
  id: string;              // payments.id
  case_id: string;
  case_no?: number | null;
  vendor_payout: number;   // 業者への支払額（手数料控除後）
  payout_at: string | null; // 振込済み日時（null=未振込）
  payout_note?: string | null;
  vendor_id: string | null;
  vendor_company: string | null;
  bank: string | null;
  branch: string | null;
  account_type: string | null;
  account: string | null;
  account_holder: string | null;
}

/** 案件詳細（管理者・全状態閲覧）。 */
export interface CaseDetail {
  photos: Array<{ path: string; url: string }>;
  order: OrderRow | null;
  payment: (PaymentRow & { paid_at?: string }) | null;
  customerName: string | null;
  vendorName: string | null;
  awardedAmount: number | null;
}

/** 手数料・税は共通ロジックに一元化（税込表示・DB一致）。 */
export { calcFee, withTax, calcPayout, TAX_RATE, FEE_RATE, yen } from "@shitamy/types";

export const adminApi = {
  async myRole(): Promise<string | null> {
    const { data: s } = await supabase.auth.getSession();
    const uid = s.session?.user.id;
    if (!uid) return null;
    const { data } = await supabase.from("profiles").select("role").eq("id", uid).single();
    return (data?.role as string) ?? null;
  },

  async myId(): Promise<string | null> {
    const { data } = await supabase.auth.getSession();
    return data.session?.user.id ?? null;
  },

  // ===== 業者審査 =====
  async listVendors(): Promise<VendorRow[]> {
    const { data, error } = await supabase
      .from("vendor_details")
      .select(
        "id, company, representative, area, status, rating, review_count, created_at, profile:profiles!vendor_details_id_fkey(email, name)",
      )
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []).map((v: Record<string, unknown>) => {
      const p = v.profile as { email?: string; name?: string } | null;
      return { ...(v as unknown as VendorRow), email: p?.email ?? null, name: p?.name ?? null };
    });
  },

  async setVendorStatus(id: string, status: VendorStatus): Promise<void> {
    const { error } = await supabase.from("vendor_details").update({ status }).eq("id", id);
    if (error) throw new Error(error.message);
  },

  // ===== 案件管理 =====
  async listCases(): Promise<CaseRow[]> {
    const { data, error } = await supabase
      .from("cases")
      .select(
        "id, case_no, status, ac_type, ai_model_guess, ai_capacity, ai_ceiling_height, ai_piping, ai_power, customer_comment, prefecture, city, awarded_amount, created_at",
      )
      .order("case_no", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);
    return (data ?? []) as CaseRow[];
  },

  /**
   * 案件の詳細（管理者）。あらゆる状態（paid含む）を閲覧できる。
   * 写真（署名URL）・発注・決済・顧客/業者名をまとめて返す。RLSは is_admin() で全件許可。
   */
  async getCaseDetail(id: string): Promise<CaseDetail> {
    // 顧客/業者名（cases → profiles）
    const { data: c } = await supabase
      .from("cases")
      .select("customer_id, vendor_id, awarded_amount, customer:profiles!cases_customer_id_fkey(name,email), vendor:profiles!cases_vendor_id_fkey(name,email)")
      .eq("id", id)
      .maybeSingle();
    const cust = (c?.customer as { name?: string; email?: string } | null) ?? null;
    const vend = (c?.vendor as { name?: string; email?: string } | null) ?? null;

    // 写真（非公開バケット→署名URL 60分）
    const { data: ph } = await supabase
      .from("case_photos").select("storage_path").eq("case_id", id).order("created_at", { ascending: true });
    const paths = (ph ?? []).map((r) => r.storage_path as string);
    let photos: Array<{ path: string; url: string }> = [];
    if (paths.length > 0) {
      const { data: signed } = await supabase.storage.from("case-photos").createSignedUrls(paths, 3600);
      photos = (signed ?? []).filter((s) => !!s.signedUrl).map((s) => ({ path: s.path ?? "", url: s.signedUrl as string }));
    }

    // 発注・決済
    const { data: order } = await supabase
      .from("orders")
      .select("id, case_id, maker, model, qty, ship_to, recipient_name, recipient_address, status, created_at")
      .eq("case_id", id).order("created_at", { ascending: false }).maybeSingle();
    const { data: payment } = await supabase
      .from("payments")
      .select("id, case_id, amount, fee_amount, vendor_payout, status, paid_at, payout_at")
      .eq("case_id", id).order("paid_at", { ascending: false }).maybeSingle();

    return {
      photos,
      order: (order as unknown as OrderRow) ?? null,
      payment: (payment as unknown as PaymentRow & { paid_at?: string }) ?? null,
      customerName: cust?.name || cust?.email || null,
      vendorName: vend?.name || vend?.email || null,
      awardedAmount: (c?.awarded_amount as number | null) ?? null,
    };
  },

  /** 案件の修正（管理者）。指定フィールドのみ更新。 */
  async updateCase(id: string, patch: CasePatch): Promise<void> {
    const { error } = await supabase.from("cases").update(patch).eq("id", id);
    if (error) throw new Error(error.message);
  },

  /**
   * 案件の完全削除（管理者）。SECURITY DEFINER RPC 経由で強制削除する。
   * 直接 delete では payments(NO ACTION) の FK 違反で決済済み案件が消えなかったため、
   * admin_delete_case(sql/34) で payments/notifications を掃除→cases削除（残りは CASCADE）。
   * ゲスト作成案件・paid案件も含め、顧客・業者・管理すべての画面から消える。
   */
  async deleteCase(id: string): Promise<void> {
    const { error } = await supabase.rpc("admin_delete_case", { p_case_id: id });
    if (error) throw new Error(error.message);
  },

  // ===== アカウント管理 =====
  async listProfiles(): Promise<ProfileRow[]> {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, email, name, role, prefecture, created_at")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as ProfileRow[];
  },

  /** ロール変更（customer / vendor / admin）。 */
  async setProfileRole(id: string, role: Role): Promise<void> {
    const { error } = await supabase.from("profiles").update({ role }).eq("id", id);
    if (error) throw new Error(error.message);
  },

  /**
   * アカウント完全削除。auth.users の削除は service role が必要なため
   * Edge Function(admin-delete-user) を経由（呼び出し元が管理者か関数側で検証）。
   */
  async deleteAccount(userId: string): Promise<void> {
    const { data: s } = await supabase.auth.getSession();
    const token = s.session?.access_token;
    if (!token) throw new Error("ログインが必要です");
    const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-delete-user`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ user_id: userId }),
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.error || `削除に失敗しました (${res.status})`);
    }
  },

  // ===== メーカー直発注 =====
  async listOrders(): Promise<OrderRow[]> {
    const { data, error } = await supabase
      .from("orders")
      .select("id, case_id, maker, model, qty, ship_to, recipient_name, recipient_address, status, created_at, case:cases!orders_case_id_fkey(case_no)")
      .order("created_at", { ascending: false })
      .limit(300);
    if (error) throw new Error(error.message);
    return (data ?? []).map((o: Record<string, unknown>) => {
      const c = o.case as { case_no?: number } | null;
      return { ...(o as unknown as OrderRow), case_no: c?.case_no ?? null };
    });
  },

  /** 配送先切替（業者宛/顧客宅）。受取人は当該当事者から自動セット（DB関数）。 */
  async setOrderShipTo(orderId: string, shipTo: "vendor" | "customer"): Promise<void> {
    const { error } = await supabase.rpc("set_order_ship_to", { p_order_id: orderId, p_ship_to: shipTo });
    if (error) throw new Error(error.message);
  },

  /** 発注ステータス更新（draft/sent/shipped/received）。 */
  async setOrderStatus(orderId: string, status: OrderRow["status"]): Promise<void> {
    const { error } = await supabase.from("orders").update({ status }).eq("id", orderId);
    if (error) throw new Error(error.message);
  },

  /**
   * 発注メールを「知らす名義」で生成（send=falseは送信前確認用のドラフト生成）。
   * 運営が編集した subject/body を渡すと、その内容で送信記録（status→sent）する。
   * Edge Function(order-email) 経由。
   */
  async generateOrderEmail(
    orderId: string,
    opts: { send?: boolean; subject?: string; body?: string; to?: string } = {},
  ): Promise<{ subject: string; body: string; status: string; to: string | null; maker: string | null; sent: boolean; note?: string }> {
    const { data: s } = await supabase.auth.getSession();
    const token = s.session?.access_token;
    if (!token) throw new Error("ログインが必要です");
    const res = await fetch(`${SUPABASE_URL}/functions/v1/order-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` },
      body: JSON.stringify({ order_id: orderId, send: opts.send ?? false, subject: opts.subject, body: opts.body, to: opts.to }),
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(j.error || `生成に失敗しました (${res.status})`);
    return { subject: j.subject, body: j.body, status: j.status, to: j.to ?? null, maker: j.maker ?? null, sent: !!j.sent, note: j.note };
  },

  // ===== メーカー（仕入先）マスタ =====
  async listMakers(): Promise<MakerRow[]> {
    const { data, error } = await supabase
      .from("makers")
      .select("id, name, contact, note, created_at")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as MakerRow[];
  },

  async createMaker(input: MakerInput): Promise<void> {
    const { error } = await supabase.from("makers").insert({
      name: input.name.trim(),
      contact: input.contact?.trim() || null,
      note: input.note?.trim() || null,
    });
    if (error) throw new Error(error.message);
  },

  async updateMaker(id: string, input: MakerInput): Promise<void> {
    const { error } = await supabase
      .from("makers")
      .update({ name: input.name.trim(), contact: input.contact?.trim() || null, note: input.note?.trim() || null })
      .eq("id", id);
    if (error) throw new Error(error.message);
  },

  async deleteMaker(id: string): Promise<void> {
    const { error } = await supabase.from("makers").delete().eq("id", id);
    if (error) throw new Error(error.message);
  },

  // ===== 決済（収納代行型 状態機械） =====
  /** 決済一覧。GMV/手数料Revenue/ステータス別の集計に利用。テーブル未整備時は空配列。 */
  async listPayments(): Promise<PaymentRow[]> {
    const { data, error } = await supabase
      .from("payments")
      .select("id, case_id, amount, fee_amount, vendor_payout, status, created_at, payout_at, case:cases!payments_case_id_fkey(case_no)")
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) return [];
    return (data ?? []).map((p: Record<string, unknown>) => {
      const c = p.case as { case_no?: number } | null;
      return { ...(p as unknown as PaymentRow), case_no: c?.case_no ?? null };
    });
  },

  // ===== 外注費の支払管理（元請モデル：業者へ銀行振込。プラットフォームで分配はしない） =====
  /** 顧客決済済み(released)の外注費一覧＋業者の振込先口座。未振込/振込済みを含む。 */
  async listPayouts(): Promise<PayoutRow[]> {
    const { data, error } = await supabase
      .from("payments")
      .select("id, case_id, vendor_payout, payout_at, payout_note, status, case:cases!payments_case_id_fkey(case_no, vendor_id)")
      .eq("status", "released")
      .order("payout_at", { ascending: true, nullsFirst: true })
      .limit(500);
    if (error) return [];
    const rows = (data ?? []).map((p: Record<string, unknown>) => {
      const c = p.case as { case_no?: number; vendor_id?: string } | null;
      return {
        id: p.id as string,
        case_id: p.case_id as string,
        case_no: c?.case_no ?? null,
        vendor_payout: (p.vendor_payout as number) ?? 0,
        payout_at: (p.payout_at as string) ?? null,
        payout_note: (p.payout_note as string) ?? null,
        vendor_id: c?.vendor_id ?? null,
        vendor_company: null as string | null,
        bank: null as string | null, branch: null as string | null, account_type: null as string | null,
        account: null as string | null, account_holder: null as string | null,
      };
    });
    const vids = [...new Set(rows.map((r) => r.vendor_id).filter((v): v is string => !!v))];
    if (vids.length > 0) {
      const { data: vds } = await supabase
        .from("vendor_details")
        .select("id, company, bank, branch, account, account_holder, account_type")
        .in("id", vids);
      const m = new Map((vds ?? []).map((v: Record<string, unknown>) => [v.id as string, v]));
      for (const r of rows) {
        const v = r.vendor_id ? m.get(r.vendor_id) : null;
        if (v) {
          r.vendor_company = (v.company as string) ?? null;
          r.bank = (v.bank as string) ?? null; r.branch = (v.branch as string) ?? null;
          r.account_type = (v.account_type as string) ?? null; r.account = (v.account as string) ?? null;
          r.account_holder = (v.account_holder as string) ?? null;
        }
      }
    }
    return rows;
  },

  /** 外注費を「振込済み」に記録（payout_at＋任意メモ）。実際の送金は当社の銀行振込で実施。 */
  async markPayout(paymentId: string, note?: string): Promise<void> {
    const { error } = await supabase
      .from("payments")
      .update({ payout_at: new Date().toISOString(), payout_note: note ?? null })
      .eq("id", paymentId);
    if (error) throw new Error(error.message);
  },

  /** 「未振込」に戻す（誤操作の取消）。 */
  async unmarkPayout(paymentId: string): Promise<void> {
    const { error } = await supabase.from("payments").update({ payout_at: null }).eq("id", paymentId);
    if (error) throw new Error(error.message);
  },

  /** AI強化学習の状況（教師データ件数・修正ラベル・的中率・設置高さ誤差・型番学習量）。 */
  async aiLearningStats(): Promise<AiLearningStats> {
    const cnt = async (table: string) => {
      const { count } = await supabase.from(table).select("*", { count: "exact", head: true });
      return count ?? 0;
    };
    const [samples, corrections, modelCatalog] = await Promise.all([
      cnt("training_samples"), cnt("ai_corrections"), cnt("model_catalog"),
    ]);
    const { data: cf } = await supabase.from("v_ai_correction_stats").select("*");
    const { data: acc } = await supabase.from("v_ai_extraction_accuracy").select("*");
    const { data: he } = await supabase.from("v_ai_install_height_error").select("*").maybeSingle();
    return {
      samples, corrections, modelCatalog,
      correctionByField: (cf ?? []) as AiLearningStats["correctionByField"],
      extractionAccuracy: (acc ?? []) as AiLearningStats["extractionAccuracy"],
      heightError: (he ?? null) as AiLearningStats["heightError"],
    };
  },

  /** 直近の修正ログ（AIが外した箇所→正解）。学習の中身を目視確認する。 */
  async recentCorrections(limit = 30): Promise<CorrectionRow[]> {
    const { data } = await supabase
      .from("ai_corrections")
      .select("id, field, ai_value, corrected_value, corrected_role, created_at")
      .order("created_at", { ascending: false })
      .limit(limit);
    return (data ?? []) as CorrectionRow[];
  },

  /** ベンチマーク：写真をanalyze-photosにかけAI抽出を得る（DB書き込みなし）。 */
  async runBenchmark(images: Array<{ mimeType: string; data: string }>): Promise<BenchExtract> {
    const { data: s } = await supabase.auth.getSession();
    const token = s.session?.access_token ?? SUPABASE_ANON_KEY;
    const res = await fetch(`${SUPABASE_URL}/functions/v1/analyze-photos`, {
      method: "POST",
      headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` },
      body: JSON.stringify({ images }),
    });
    if (!res.ok) throw new Error("analyze-photos " + res.status);
    const j = await res.json();
    return (j?.extracted ?? {}) as BenchExtract;
  },

  /** ベンチマーク結果＋正解を保存（精度計測データとして蓄積）。 */
  async saveBenchmark(row: {
    label: string | null; extracted: BenchExtract;
    truth_model: string | null; truth_capacity: string | null; truth_install_height_m: number | null;
  }): Promise<void> {
    const { data: s } = await supabase.auth.getSession();
    const x = row.extracted;
    const { error } = await supabase.from("ai_benchmarks").insert({
      label: row.label,
      ai_extracted: x,
      ai_model: [x.maker, x.model].filter(Boolean).join(" ") || null,
      ai_capacity: x.capacity ?? null,
      ai_install_height_m: typeof x.install_height_m === "number" ? x.install_height_m : null,
      ai_catalog_matched: x.catalog_matched ?? null,
      truth_model: row.truth_model,
      truth_capacity: row.truth_capacity,
      truth_install_height_m: row.truth_install_height_m,
      created_by: s.session?.user.id ?? null,
    });
    if (error) throw new Error(error.message);
  },

  async benchmarkAccuracy(): Promise<BenchAccuracy | null> {
    const { data } = await supabase.from("v_ai_benchmark_accuracy").select("*").maybeSingle();
    return (data ?? null) as BenchAccuracy | null;
  },

  async recentBenchmarks(limit = 20): Promise<BenchRow[]> {
    const { data } = await supabase
      .from("ai_benchmarks")
      .select("id, label, ai_model, truth_model, ai_install_height_m, truth_install_height_m, created_at")
      .order("created_at", { ascending: false })
      .limit(limit);
    return (data ?? []) as BenchRow[];
  },
};

export interface BenchExtract {
  maker?: string | null; model?: string | null; capacity?: string | null;
  install_form?: string | null; refrigerant?: string | null;
  install_height_m?: number | null; install_height_basis?: string | null;
  piping?: string | null; power?: string | null; catalog_matched?: boolean;
  [k: string]: unknown;
}
export interface BenchAccuracy {
  n_model_labeled: number; n_model_match: number; model_accuracy_pct: number | null;
  n_height_labeled: number; height_mae_m: number | null;
}
export interface BenchRow {
  id: string; label: string | null; ai_model: string | null; truth_model: string | null;
  ai_install_height_m: number | null; truth_install_height_m: number | null; created_at: string;
}

export interface AiLearningStats {
  samples: number;
  corrections: number;
  modelCatalog: number;
  correctionByField: Array<{ field: string; n_corrections: number; n_cases: number; last_at: string | null }>;
  extractionAccuracy: Array<{ attribute: string; n_labeled: number; n_ai_predicted: number; n_match: number; accuracy_pct: number | null }>;
  heightError: { n: number; mae_m: number | null; max_err_m: number | null } | null;
}
export interface CorrectionRow {
  id: string;
  field: string;
  ai_value: string | null;
  corrected_value: string | null;
  corrected_role: string | null;
  created_at: string;
}

/** 後方互換エイリアス：既存ファイルは `api` を import している。 */
export const api = adminApi;
